import { useCallback, useState } from "react";
import type { FormValues } from "@/widgets/classic-form/form";
import { FormLevelFeatureType, useMetadataQuery } from "@/entities/form-metadata";
import { useUserPreferencesQuery } from "@/entities/user-preferences";
import { usePageContextQuery } from "@/shared/page-context";
import { useOpenSearchAssist } from "../listeners";
import { getOnlyNameFilled } from "./util";

const MOBILE_MAX_WIDTH = 480;

export const useSearchAssist = () => {
    const [previouslyOpened, setPreviouslyOpened] = useState(false);
    const { data: pageContext } = usePageContextQuery();
    const baseOpenSearchAssist = useOpenSearchAssist();
    const { data: formMetadata } = useMetadataQuery();
    const { data: userPreference } = useUserPreferencesQuery();

    const openSearchAssist = useCallback(() => {
        setPreviouslyOpened(true);
        baseOpenSearchAssist();
    }, [baseOpenSearchAssist]);

    const getShouldOpenSearchAssist = useCallback(
        (values: FormValues) => {
            // Don't open twice on the same form
            if (previouslyOpened) return false;
            // Don't open on mobile devices
            if (window.innerWidth <= MOBILE_MAX_WIDTH) return false;

            if (userPreference?.UserPreferences.IsSkipAssistPlacesModalPermanently) return false;
            // Global Partner Website" (partnerId=5574) is an adaptation of the US website
            const isUSPartnerId = pageContext.partnerId === 0 || pageContext.partnerId === 5574;
            if (!isUSPartnerId || pageContext.locale !== "en-US") return false;
            const hasSearchAssistEnabled = formMetadata.features.some(
                (feature) => feature.featureType === FormLevelFeatureType.SearchAssistModal,
            );
            if (!hasSearchAssistEnabled) return false;

            return getOnlyNameFilled(values);
        },
        [
            pageContext.locale,
            pageContext.partnerId,
            formMetadata.features,
            previouslyOpened,
            userPreference?.UserPreferences.IsSkipAssistPlacesModalPermanently,
        ],
    );
    return [getShouldOpenSearchAssist, openSearchAssist] as const;
};
